import { cn } from '@/lib/utils';
import { NOYAU_ORDER, type Noyau } from '@mss_creator/shared';
import type { CSSProperties } from 'react';

export interface NoyauPickerProps {
  value: Noyau | null;
  onChange: (noyau: Noyau) => void;
}

// Filled (selected) uses the noyau's solid color; PGS (jaune) keeps dark text
// implicitly via the border-only ring, matching the PMS RoundTagGroup spec.
const FILLED_CLASSES: Record<Noyau, string> = {
  GSP: 'bg-rose border-rose',
  GPS: 'bg-rouge border-rouge',
  PGS: 'bg-jaune border-jaune',
  PSG: 'bg-vert border-vert',
  SPG: 'bg-bleu-ciel border-bleu-ciel',
  SGP: 'bg-bleu-nuit border-bleu-nuit',
};

const OUTLINE_CLASSES: Record<Noyau, string> = {
  GSP: 'border-rose',
  GPS: 'border-rouge',
  PGS: 'border-jaune',
  PSG: 'border-vert',
  SPG: 'border-bleu-ciel',
  SGP: 'border-bleu-nuit',
};

/**
 * Contrast of the in-circle label against each *filled* noyau, measured against
 * the tokens in styles.css. No single text color clears WCAG AA (4.5:1, and
 * 4.5 is the right threshold because the label is 11px — not large text), so
 * the choice is per-noyau:
 *
 *   bleu-nuit #312783  blanc 12.0 ✓  noir  1.75 ✗
 *   rouge     #e30613  blanc  4.88 ✓  noir  4.30 ✗
 *   rose      #e6007e  blanc  4.50 ~  noir  4.67 ✓  (blanc sits on the line — take noir)
 *   vert      #009640  blanc  3.85 ✗  noir  5.45 ✓
 *   bleu-ciel #009fe3  blanc  2.97 ✗  noir  7.07 ✓
 *   jaune     #efd500  blanc  1.48 ✗  noir 14.2  ✓
 */
const FILLED_LABEL_CLASSES: Record<Noyau, string> = {
  GSP: 'text-noir',
  GPS: 'text-blanc',
  PGS: 'text-noir',
  PSG: 'text-noir',
  SPG: 'text-noir',
  SGP: 'text-blanc',
};

/**
 * Single-select noyau picker — inspired by the PMS `RoundTagGroup` legend
 * component. Selected pastille scales up filled; unselected stay outline-only.
 * `p-1` on the radiogroup gives the scaled-up selected pastille breathing
 * room so its left edge never sits flush against the row's own bounding box.
 *
 * The noyau code is revealed *inside* the pastille on hover — it used to be a
 * Radix `Tooltip` floating above it. Two consequences of that swap:
 *
 * 1. The reveal must fire on `focus-visible` as well as `hover`. Radix
 *    `Tooltip` did this for free; a bare `group-hover:` would silently drop
 *    keyboard users (WCAG SC 2.1.1). Screen readers are unaffected either way
 *    — they read `aria-label`, which is why the label span is `aria-hidden`.
 * 2. The label sits *on* the noyau color when selected, so it needs a
 *    per-noyau text color (see `FILLED_LABEL_CLASSES`). Unselected pastilles
 *    are transparent-on-white, where `text-noir` is 21:1 and always safe.
 *
 * Known, deliberately out of scope: the unselected `border-jaune` (1.48:1) and
 * `border-bleu-ciel` (2.97:1) rings fail SC 1.4.11 for non-text contrast.
 * Fixing means darkening brand colors to their `-deep` tokens, which is a PMS
 * token decision rather than a component one.
 */
export function NoyauPicker({ value, onChange }: NoyauPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Noyau"
      className="flex gap-4 p-1"
      style={
        {
          // Re-pin to the invariant tokens (styles.css) so this component's real,
          // literal bleu-nuit swatch always renders the true brand color, even
          // when rendered under a NoyauAccentScope ancestor override (point 16).
          '--color-bleu-nuit': 'var(--color-bleu-nuit-true)',
          '--color-bleu-nuit-tint': 'var(--color-bleu-nuit-true-tint)',
          '--color-bleu-nuit-deep': 'var(--color-bleu-nuit-true-deep)',
        } as CSSProperties
      }
    >
      {NOYAU_ORDER.map((noyau) => {
        const selected = value === noyau;
        return (
          <button
            key={noyau}
            type="button"
            // biome-ignore lint/a11y/useSemanticElements: a real <input type="radio"> can't host the in-circle label span or the fill/scale treatment. The rule only started firing now that the button has children — the role itself has been here all along.
            role="radio"
            aria-checked={selected}
            aria-label={noyau}
            onClick={() => onChange(noyau)}
            className={cn(
              'group relative h-11 w-11 rounded-full border-2 bg-transparent transition-transform duration-100',
              selected
                ? `${FILLED_CLASSES[noyau]} scale-110`
                : `${OUTLINE_CLASSES[noyau]} hover:scale-105`,
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-0 flex items-center justify-center font-semibold text-micro opacity-0 transition-opacity duration-100',
                'group-hover:opacity-100 group-focus-visible:opacity-100',
                selected ? FILLED_LABEL_CLASSES[noyau] : 'text-noir',
              )}
            >
              {noyau}
            </span>
          </button>
        );
      })}
    </div>
  );
}
